import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, CheckCircle, CircleHelp, Clock, Download, Tags, TriangleAlert, UserRound, type LucideIcon } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { KpiCard, KpiGrid, type KpiTone } from "../components/KpiCard";
import { FormField, inputClass, selectClass } from "../components/FormField";
import {
  fetchAdminActsReview,
  fetchAdminHospitalActsSummaries,
  updateAdminHospitalActCategory,
  type AdminActsReviewAct,
  type AdminActsReviewStats,
} from "../api/adminApi";
import {
  reviewHospitalMedicalActs,
  updateHospitalActCategory,
  type HospitalActsReviewAct,
} from "../api/hospitalApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import {
  categoryValidationCategoryOptions,
  categoryValidationFilterOptions,
  categoryValidationHospitalOptions,
  categoryValidationStats,
  categoryValidations,
} from "../data";
import { CategoryActionSelect } from "../components/CategoryActionSelect";
import { cn } from "@/shared/lib/cn";
import type { CategoryValidation, CategoryValidationFilter } from "../types";

const summaryStyles: Record<CategoryValidationFilter, { tone: KpiTone; icon: LucideIcon }> = {
  toValidate: { tone: "amber", icon: Clock },
  classified: { tone: "purple", icon: Tags },
  manuallyValidated: { tone: "mint", icon: CheckCircle },
  lowConfidence: { tone: "pink", icon: TriangleAlert },
  unclassified: { tone: "slate", icon: CircleHelp },
};

function toConfidencePercent(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value <= 1 ? Math.round(value * 100) : Math.round(value);
}

function mapReviewAct(row: AdminActsReviewAct | HospitalActsReviewAct): CategoryValidation {
  const filterStatus = (row.filter_status || "toValidate") as CategoryValidationFilter;
  const category = row.care_category || "consultation";
  const source = (row.care_category_source || "rule").toLowerCase();

  return {
    id: String(row.id),
    code: row.code,
    label: row.label || ("libelle" in row ? row.libelle : "") || "",
    hospital: row.hospital || "",
    hospitalId: row.hospital_id,
    filterStatus:
      filterStatus === "classified" ||
      filterStatus === "manuallyValidated" ||
      filterStatus === "lowConfidence" ||
      filterStatus === "unclassified" ||
      filterStatus === "toValidate"
        ? filterStatus
        : "toValidate",
    proposedCategory: category,
    confidence: toConfidencePercent(row.care_category_confidence),
    confidenceSource: source === "ai" ? "ai" : "rule",
    selectedCategory: category,
  };
}

export function CategoryValidationPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.acts.categoryValidation;
  const resultLabels = labels.results;
  const { showToast } = usePageActions();
  const isHospitalAccount = getStoredAccountType() === "hospital";

  const [items, setItems] = useState<CategoryValidation[]>(categoryValidations);
  const [stats, setStats] = useState<AdminActsReviewStats>(categoryValidationStats);
  const [usingApi, setUsingApi] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hospitalOptions, setHospitalOptions] = useState(categoryValidationHospitalOptions);

  const [draftHospital, setDraftHospital] = useState("");
  const [draftFilter, setDraftFilter] = useState<CategoryValidationFilter>("toValidate");
  const [draftCategory, setDraftCategory] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const [appliedHospital, setAppliedHospital] = useState("");
  const [appliedFilter, setAppliedFilter] = useState<CategoryValidationFilter>("toValidate");
  const [appliedCategory, setAppliedCategory] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setItems(categoryValidations);
      setStats(categoryValidationStats);
      setHospitalOptions(categoryValidationHospitalOptions);
      return;
    }

    setLoading(true);
    try {
      if (isHospitalAccount) {
        const api = await reviewHospitalMedicalActs({
          filter: appliedFilter,
          q: appliedSearch || undefined,
        });
        setItems((api.medical_acts ?? []).map(mapReviewAct));
        setStats(api.stats ?? categoryValidationStats);
        setHospitalOptions([{ value: "", label: "Mon établissement" }]);
        setUsingApi(true);
        return;
      }

      const hospitalId = appliedHospital ? Number.parseInt(appliedHospital, 10) : undefined;
      const [api, summaries] = await Promise.all([
        fetchAdminActsReview({
          hospital_id: hospitalId && !Number.isNaN(hospitalId) ? hospitalId : undefined,
          filter: appliedFilter,
          q: appliedSearch || undefined,
        }),
        fetchAdminHospitalActsSummaries().catch(() => null),
      ]);
      setItems((api.medical_acts ?? []).map(mapReviewAct));
      setStats(api.stats ?? categoryValidationStats);
      if (summaries?.hospitals?.length) {
        setHospitalOptions([
          { value: "", label: "Tous" },
          ...summaries.hospitals.map((h) => ({
            value: String(h.id),
            label: h.hospital,
          })),
        ]);
      }
      setUsingApi(true);
    } catch {
      setUsingApi(false);
      setItems(categoryValidations);
      setStats(categoryValidationStats);
      setHospitalOptions(categoryValidationHospitalOptions);
    } finally {
      setLoading(false);
    }
  }, [appliedHospital, appliedFilter, appliedSearch, isHospitalAccount]);

  useEffect(() => {
    void load();
  }, [load]);

  const summaryCards = [
    { key: "toValidate" as const, value: stats.toValidate },
    { key: "classified" as const, value: stats.classified },
    { key: "manuallyValidated" as const, value: stats.manuallyValidated },
    { key: "lowConfidence" as const, value: stats.lowConfidence },
    { key: "unclassified" as const, value: stats.unclassified },
  ];

  const applyFilters = () => {
    setAppliedHospital(draftHospital);
    setAppliedFilter(draftFilter);
    setAppliedCategory(draftCategory);
    setAppliedSearch(draftSearch);
  };

  const handleSummaryClick = (filter: CategoryValidationFilter) => {
    setDraftFilter(filter);
    setAppliedFilter(filter);
  };

  const filtered = useMemo(() => {
    if (usingApi) {
      return items.filter((item) => {
        if (!appliedCategory) return true;
        const categoryOption = categoryValidationCategoryOptions.find(
          (option) => option.value === appliedCategory,
        );
        return (
          item.selectedCategory === appliedCategory ||
          item.proposedCategory
            .toLowerCase()
            .includes((categoryOption?.label ?? appliedCategory).toLowerCase())
        );
      });
    }

    return items.filter((item) => {
      const hospitalOption = hospitalOptions.find((option) => option.value === appliedHospital);
      const byHospital = appliedHospital
        ? item.hospital.toLowerCase().includes((hospitalOption?.label ?? "").toLowerCase())
        : true;
      const byFilter = item.filterStatus === appliedFilter;
      const categoryOption = categoryValidationCategoryOptions.find(
        (option) => option.value === appliedCategory,
      );
      const byCategory = appliedCategory
        ? item.proposedCategory.toLowerCase().includes((categoryOption?.label ?? "").toLowerCase())
        : true;
      const bySearch = appliedSearch
        ? `${item.code} ${item.label}`.toLowerCase().includes(appliedSearch.toLowerCase())
        : true;
      return byHospital && byFilter && byCategory && bySearch;
    });
  }, [items, appliedHospital, appliedFilter, appliedCategory, appliedSearch, usingApi, hospitalOptions]);

  const handleCategoryChange = (id: string, selectedCategory: string) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, selectedCategory } : item)),
    );
  };

  const handleConfirm = async (id: string, status: "manuallyValidated" | "classified") => {
    const item = items.find((row) => row.id === id);
    if (!item) return;

    if (!getAuthToken() || !usingApi) {
      setItems((current) =>
        current.map((row) => (row.id === id ? { ...row, filterStatus: status } : row)),
      );
      return;
    }

    if (!isHospitalAccount && !item.hospitalId) {
      setItems((current) =>
        current.map((row) => (row.id === id ? { ...row, filterStatus: status } : row)),
      );
      return;
    }

    try {
      const action = status === "classified" ? "confirm" : "correct";
      const payload = {
        action: action as "confirm" | "correct",
        care_category: action === "correct" ? item.selectedCategory : undefined,
      };
      if (isHospitalAccount) {
        await updateHospitalActCategory(Number(item.id), payload);
      } else {
        await updateAdminHospitalActCategory(item.hospitalId!, Number(item.id), payload);
      }
      await load();
      showToast({ message: resultLabels.ok });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Mise à jour impossible.";
      showToast({ message, variant: "warning" });
    }
  };

  const handleExportCsv = () => {
    const rows = [
      Object.values(resultLabels.columns).join(","),
      ...filtered.map((item) =>
        [
          item.hospital,
          item.code,
          item.label,
          item.proposedCategory,
          `${item.confidence}% (${resultLabels.confidenceSource[item.confidenceSource]})`,
        ]
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      ),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "validation-categories.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const resultsCount =
    appliedFilter === "toValidate" && !usingApi ? stats.toValidate : filtered.length;

  return (
    <>
      <Breadcrumbs
        homeHref={isHospitalAccount ? "/dashboard/hospital" : undefined}
        items={[
          { label: t.safecheck.nav.hospitalActs, href: "/dashboard/acts/by-hospital" },
          { label: labels.title },
        ]}
      />

      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
        {loading && <p className="mt-1 text-xs text-muted">Chargement…</p>}
      </div>

      <KpiGrid className="mb-5 grid-cols-1 sm:grid-cols-2 xl:grid-cols-5">
        {summaryCards.map((card) => {
          const style = summaryStyles[card.key];

          return (
            <KpiCard
              key={card.key}
              tone={style.tone}
              icon={style.icon}
              label={labels.summary[card.key]}
              value={card.value}
              active={appliedFilter === card.key}
              onClick={() => handleSummaryClick(card.key)}
            />
          );
        })}
      </KpiGrid>

      <div className="header-ribbon filter-panel relative isolate rounded-[var(--radius-card)] mb-5 p-5">
        <div
          className={cn(
            "grid grid-cols-1 gap-4 lg:items-end",
            isHospitalAccount
              ? "lg:grid-cols-[1fr_1fr_1fr_auto]"
              : "lg:grid-cols-[1fr_1fr_1fr_1fr_auto]",
          )}
        >
          {!isHospitalAccount && (
            <FormField label={labels.fields.hospital}>
              <select
                value={draftHospital}
                onChange={(event) => setDraftHospital(event.target.value)}
                className={selectClass}
              >
                {hospitalOptions.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          <FormField label={labels.fields.filter}>
            <select
              value={draftFilter}
              onChange={(event) => setDraftFilter(event.target.value as CategoryValidationFilter)}
              className={selectClass}
            >
              {categoryValidationFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label={labels.fields.currentCategory}>
            <select
              value={draftCategory}
              onChange={(event) => setDraftCategory(event.target.value)}
              className={selectClass}
            >
              {categoryValidationCategoryOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label={labels.fields.search}>
            <input
              value={draftSearch}
              onChange={(event) => setDraftSearch(event.target.value)}
              placeholder={labels.searchPlaceholder}
              className={inputClass}
            />
          </FormField>

          <button
            type="button"
            onClick={applyFilters}
            className="h-10 rounded-lg bg-primary px-5 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {labels.applyFilter}
          </button>
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={handleExportCsv}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
          >
            <Download size={15} />
            {labels.exportCsv}
          </button>
        </div>
      </div>

      <div className="theme-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-foreground">{resultLabels.title}</h2>
            <span className="flex h-6 min-w-6 items-center justify-center rounded-md bg-purple-soft px-2 text-xs font-semibold text-purple">
              {resultsCount}
            </span>
          </div>
          <p className="text-sm text-muted">{resultLabels.hint}</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {Object.values(resultLabels.columns).map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                  <td className="px-4 py-3 text-sm text-foreground">
                    {isHospitalAccount ? item.hospital || "—" : item.hospital}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-pink">{item.code}</td>
                  <td className="px-4 py-3 text-sm font-medium uppercase text-foreground">{item.label}</td>
                  <td className="px-4 py-3">
                    <span
                      title={item.proposedCategory}
                      className="inline-flex max-w-[240px] items-center gap-2 rounded-md bg-card-muted px-2.5 py-1.5 text-[10px] font-semibold uppercase leading-snug text-muted-foreground"
                    >
                      <UserRound size={12} className="shrink-0" />
                      <span className="line-clamp-2">{item.proposedCategory}</span>
                      <AlertTriangle size={12} className="shrink-0 text-yellow" />
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {item.confidence}% ({resultLabels.confidenceSource[item.confidenceSource]})
                  </td>
                  <td className="px-4 py-3">
                    {item.filterStatus === "toValidate" || item.filterStatus === "lowConfidence" ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <CategoryActionSelect
                          value={item.selectedCategory}
                          onChange={(selectedCategory) => handleCategoryChange(item.id, selectedCategory)}
                        />
                        <button
                          type="button"
                          onClick={() => void handleConfirm(item.id, "manuallyValidated")}
                          className="inline-flex h-9 items-center rounded-lg bg-primary px-3 text-xs font-semibold text-white transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          {resultLabels.ok}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleConfirm(item.id, "classified")}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-teal/50 text-teal transition-colors hover:bg-teal-soft"
                        >
                          <Check size={14} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
